/**
 * Sun — WinterMute's visible body.
 *
 * Used in two places, which is why it takes presence as a prop rather than
 * owning any session state:
 *
 *  - The Rite, filling the screen on black while it speaks.
 *  - The Watcher indicator, small and dim over the live camera, visible ONLY
 *    while a VLM request is genuinely in flight.
 *
 * The blink is autonomous. It fires on a randomised interval so it never falls
 * into a rhythm the eye can predict, which is most of what makes it read as
 * alive rather than as a loading animation.
 */

import { useEffect, useRef } from 'react';
import { SunRenderer, type SunFrame } from './SunRenderer.js';
import { loadIcon } from '../Iconostasis/loadIcon.js';
import { PS1 } from '../postfx/ps1.js';
import { observeCanvasSize } from '../canvasSize.js';

export interface SunProps {
  /** 0 hidden .. 1 fully present. Animated by the caller. */
  presence: number;
  /** Brightness multiplier on top of presence. */
  gain?: number;
  /** Diameter as a fraction of the smaller viewport edge. */
  sizeFraction?: number;
  /** Vertical centre as a fraction of viewport height. */
  centreY?: number;
  /** Mean seconds between blinks. Randomised +/- 50%. */
  blinkEvery?: number;
  /** Deliberate double-blink, used when the Watcher starts looking. */
  blinkNow?: number;
  className?: string;
}

const BLINK_DOWN = 0.11;
const BLINK_HOLD = 0.045;
const BLINK_UP = 0.19;
const BLINK_TOTAL = BLINK_DOWN + BLINK_HOLD + BLINK_UP;

export function Sun({
  presence,
  gain = 1,
  sizeFraction = 1.35,
  centreY = 0.42,
  blinkEvery = 5.5,
  blinkNow = 0,
  className,
}: SunProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Props are read through a ref so the rAF loop never restarts on a prop change;
  // restarting it would reset the sun's clock and jump the rotation.
  const live = useRef({ presence, gain, sizeFraction, centreY, blinkEvery, blinkNow });
  live.current = { presence, gain, sizeFraction, centreY, blinkEvery, blinkNow };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const r = new SunRenderer(canvas);
    if (!r.ready) return;

    let disposed = false;
    void loadIcon('eye')
      .then((c) => {
        if (!disposed) r.upload(c);
      })
      .catch((e) => console.warn('[Sun] eye.json failed to load:', e));

    const unobserve = observeCanvasSize(canvas, (w, h, dpr) => r.resize(w, h, dpr));

    let raf = 0;
    const t0 = performance.now();
    let nextBlink = 1.2;
    let blinkStart = -1;
    let lastForced = 0;

    const loop = (now: number) => {
      const t = (now - t0) / 1000;
      const p = live.current;

      // An externally requested blink (the Watcher starting to look) preempts
      // the schedule.
      if (p.blinkNow > lastForced) {
        lastForced = p.blinkNow;
        blinkStart = t;
        nextBlink = t + BLINK_TOTAL + 0.25;
      }
      if (blinkStart < 0 && t >= nextBlink) {
        blinkStart = t;
      }

      let blink = 0;
      if (blinkStart >= 0) {
        const e = t - blinkStart;
        if (e < BLINK_DOWN) blink = e / BLINK_DOWN;
        else if (e < BLINK_DOWN + BLINK_HOLD) blink = 1;
        else if (e < BLINK_TOTAL) blink = 1 - (e - BLINK_DOWN - BLINK_HOLD) / BLINK_UP;
        else {
          blink = 0;
          blinkStart = -1;
          const mean = Math.max(1.2, p.blinkEvery);
          nextBlink = t + mean * (0.5 + Math.random());
        }
      }

      const minEdge = Math.min(window.innerWidth, window.innerHeight);
      const frame: SunFrame = {
        timeSec: t,
        presence: clamp01(p.presence),
        blink,
        cx: window.innerWidth / 2,
        cy: window.innerHeight * p.centreY,
        size: minEdge * p.sizeFraction,
        gain: p.gain,
        snap: PS1.enabled ? PS1.snapPx : 0,
      };
      r.draw(frame);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      unobserve();
      r.dispose();
    };
  }, []);

  return <canvas ref={canvasRef} className={className ?? 'sun-canvas'} />;
}

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}
